import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiUser, FiMail, FiPhone, FiMapPin, FiCamera, FiLock, FiBell, 
  FiGlobe, FiCreditCard, FiShield, FiActivity, FiSave, FiX,
  FiEdit3, FiKey, FiEye, FiEyeOff, FiUpload, FiTrash2, FiBook,
  FiDollarSign, FiCalendar, FiClock, FiLink, FiCheckCircle,
  FiAlertCircle, FiInfo, FiHelpCircle, FiLogOut, FiCheck
} = FiIcons;

const ProfileSettings = () => {
  const { user, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [passwordVisible, setPasswordVisible] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Profile photo state
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    localStorage.getItem(`profilePhoto_${user?.email}`) || null
  );

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    bio: '',
    jobTitle: '',
    company: '',
    website: ''
  });

  // Load profile data from localStorage or user context
  useEffect(() => {
    const savedProfile = localStorage.getItem(`profile_${user?.email}`);
    if (savedProfile) {
      setProfileData(JSON.parse(savedProfile));
    } else {
      // Initialize with user data or defaults
      setProfileData({
        firstName: user?.firstName || user?.name?.split(' ')[0] || 'Sarah',
        lastName: user?.lastName || user?.name?.split(' ')[1] || 'Mitchell',
        email: user?.email || 'sarah.mitchell@dentalpractice.com',
        phone: user?.phone || '(555) 123-4567',
        address: user?.address || '123 Dental Plaza',
        city: user?.city || 'New York',
        state: user?.state || 'NY',
        zip: user?.zip || '10001',
        country: user?.country || 'United States',
        bio: user?.bio || 'Experienced dental practitioner with 10+ years in restorative dentistry.',
        jobTitle: user?.jobTitle || 'Dental Practitioner',
        company: user?.company || 'DentalBridge Practice',
        website: user?.website || 'www.dentalpractice.com'
      });
    }
  }, [user]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notificationSettings, setNotificationSettings] = useState(() => {
    const saved = localStorage.getItem(`notifications_${user?.email}`);
    return saved ? JSON.parse(saved) : {
      emailNotifications: true,
      smsNotifications: false,
      referralUpdates: true,
      dealroomActivity: true,
      communityMentions: true,
      newsletter: true,
      securityAlerts: true,
      appointmentReminders: true,
      paymentReminders: false
    };
  });

  const [privacySettings, setPrivacySettings] = useState(() => {
    const saved = localStorage.getItem(`privacy_${user?.email}`);
    return saved ? JSON.parse(saved) : {
      profileVisibility: 'public',
      showEmail: false,
      showPhone: false,
      activityStatus: 'online',
      allowMessaging: true,
      showInDirectory: true
    };
  });

  const [billingInfo, setBillingInfo] = useState({
    cardNumber: '**** **** **** 1234',
    expiryDate: '12/25',
    cardHolder: 'Sarah Mitchell',
    billingAddress: '123 Dental Plaza, New York, NY 10001'
  });

  const [subscription, setSubscription] = useState({
    plan: 'Professional',
    status: 'active',
    nextBilling: '2024-12-15',
    amount: '$99.99/month'
  });

  // Track changes
  useEffect(() => {
    const savedProfile = localStorage.getItem(`profile_${user?.email}`);
    if (savedProfile) {
      const saved = JSON.parse(savedProfile);
      const changed = JSON.stringify(saved) !== JSON.stringify(profileData);
      setHasChanges(changed);
    }
  }, [profileData, user?.email]);

  // Handle profile photo change
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit');
        return;
      }
      
      setProfilePhoto(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setPreviewUrl(dataUrl);
        // Save to localStorage immediately
        localStorage.setItem(`profilePhoto_${user?.email}`, dataUrl);
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove profile photo
  const removeProfilePhoto = () => {
    setProfilePhoto(null);
    setPreviewUrl(null);
    localStorage.removeItem(`profilePhoto_${user?.email}`);
    setHasChanges(true);
    
    // Reset file input
    const fileInput = document.getElementById('profile-photo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      // Save to localStorage
      localStorage.setItem(`profile_${user?.email}`, JSON.stringify(profileData));
      
      // Update user context if updateUser function exists
      if (updateUser) {
        updateUser({
          ...user,
          ...profileData,
          name: `${profileData.firstName} ${profileData.lastName}`
        });
      }
      
      setIsSaving(false);
      setSaveSuccess(true);
      setHasChanges(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 1000);
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    // Validate password requirements
    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    // Simulate password change
    setIsSaving(true);
    setTimeout(() => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);
      setIsSaving(false);
      alert('Password updated successfully!');
    }, 1000);
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisible(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleNotificationChange = (setting) => {
    const updated = {
      ...notificationSettings,
      [setting]: !notificationSettings[setting]
    };
    setNotificationSettings(updated);
    localStorage.setItem(`notifications_${user?.email}`, JSON.stringify(updated));
  };

  const handlePrivacyChange = (setting, value) => {
    const updated = {
      ...privacySettings,
      [setting]: value
    };
    setPrivacySettings(updated);
    localStorage.setItem(`privacy_${user?.email}`, JSON.stringify(updated));
  };

  const getProfileCompletion = () => {
    const totalFields = Object.keys(profileData).length;
    const filledFields = Object.values(profileData).filter(value => value && value.trim() !== '').length;
    return Math.round((filledFields / totalFields) * 100);
  };

  // Get profile image URL (preview or default)
  const getProfileImageUrl = () => {
    if (previewUrl) return previewUrl;
    return null;
  };

  // Get initials for avatar
  const getInitials = () => {
    return `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}`.toUpperCase();
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      alert('Account deletion requested. You will receive an email to confirm this action.');
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
              <p className="text-gray-600 mt-1">Manage your account information, preferences, and security</p>
            </div>
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 px-4 py-2 rounded-lg"
              >
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800 font-medium">Unsaved changes</span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Success Message */}
        <AnimatePresence>
          {saveSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3"
            >
              <div className="p-2 bg-green-500 rounded-full">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-green-900">Changes saved successfully!</p>
                <p className="text-sm text-green-700">Your profile has been updated.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-6">
              {/* Profile Header */}
              <div className="p-6 bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <div className="relative">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 overflow-hidden">
                    {getProfileImageUrl() ? (
                      <img 
                        src={getProfileImageUrl()} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-blue-600">
                        {getInitials()}
                      </span>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-1/2 transform translate-x-6 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                    <SafeIcon icon={FiCamera} className="w-4 h-4 text-gray-600" />
                    <input
                      id="profile-photo-upload-sidebar"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <h2 className="text-xl font-semibold text-center">
                  {profileData.firstName} {profileData.lastName}
                </h2>
                <p className="text-blue-100 text-center text-sm mt-1">
                  {profileData.jobTitle}
                </p>
                <div className="mt-4 bg-white bg-opacity-20 rounded-lg p-3">
                  <div className="flex justify-between text-sm">
                    <span>Profile Completion</span>
                    <span>{getProfileCompletion()}%</span>
                  </div>
                  <div className="mt-2 w-full bg-white bg-opacity-30 rounded-full h-2">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${getProfileCompletion()}%` }}
                      transition={{ duration: 0.5 }}
                      className="bg-white h-2 rounded-full"
                    ></motion.div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="p-4">
                {[
                  { id: 'profile', label: 'Profile Information', icon: FiUser },
                  { id: 'security', label: 'Security', icon: FiLock },
                  { id: 'notifications', label: 'Notifications', icon: FiBell },
                  { id: 'privacy', label: 'Privacy', icon: FiShield },
                  { id: 'billing', label: 'Billing & Subscription', icon: FiCreditCard },
                  { id: 'account', label: 'Account Management', icon: FiActivity }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
              </div>
              
              {/* Logout Button */}
              <div className="p-4 border-t border-gray-100">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors"
                >
                  <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                  <span className="font-medium">Log Out</span>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'profile' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>
                    <form onSubmit={handleProfileUpdate} className="space-y-6">
                      {/* Profile Photo Section */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <SafeIcon icon={FiCamera} className="w-5 h-5 text-blue-600" />
                          Profile Photo
                        </h3>
                        <div className="flex items-center space-x-4">
                          <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 border-2 border-gray-300">
                            {getProfileImageUrl() ? (
                              <img 
                                src={getProfileImageUrl()} 
                                alt="Profile Preview" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100">
                                <span className="text-2xl font-bold text-blue-600">
                                  {getInitials()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-2">
                              JPG, GIF or PNG. Max size of 5MB.
                            </p>
                            <div className="flex space-x-2">
                              <label className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 cursor-pointer transition-colors flex items-center gap-2">
                                <SafeIcon icon={FiUpload} className="w-4 h-4" />
                                Upload New
                                <input
                                  id="profile-photo-upload"
                                  type="file"
                                  accept="image/*"
                                  onChange={handlePhotoChange}
                                  className="hidden"
                                />
                              </label>
                              {previewUrl && (
                                <button
                                  type="button"
                                  onClick={removeProfilePhoto}
                                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                                >
                                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            First Name *
                          </label>
                          <input
                            type="text"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            value={profileData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            value={profileData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Job Title
                          </label>
                          <input
                            type="text"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            value={profileData.jobTitle}
                            onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Company
                          </label>
                          <input
                            type="text"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            value={profileData.company}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          value={profileData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          value={profileData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          value={profileData.website}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          placeholder="https://www.example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Bio
                        </label>
                        <textarea
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          value={profileData.bio}
                          onChange={(e) => handleInputChange('bio', e.target.value)}
                          placeholder="Tell us about yourself..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {profileData.bio.length} characters
                        </p>
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiMapPin} className="w-5 h-5 text-blue-600" />
                          Address Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Street Address
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              value={profileData.address}
                              onChange={(e) => handleInputChange('address', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              City
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              value={profileData.city}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              State
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              value={profileData.state}
                              onChange={(e) => handleInputChange('state', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              ZIP Code
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              value={profileData.zip}
                              onChange={(e) => handleInputChange('zip', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Country
                            </label>
                            <select
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              value={profileData.country}
                              onChange={(e) => handleInputChange('country', e.target.value)}
                            >
                              <option>United States</option>
                              <option>Canada</option>
                              <option>United Kingdom</option>
                              <option>Australia</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-6 border-t">
                        <button
                          type="button"
                          onClick={() => {
                            const savedProfile = localStorage.getItem(`profile_${user?.email}`);
                            if (savedProfile) {
                              setProfileData(JSON.parse(savedProfile));
                            }
                            setHasChanges(false);
                          }}
                          className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <motion.button
                          type="submit"
                          disabled={isSaving || !hasChanges}
                          whileHover={{ scale: hasChanges ? 1.02 : 1 }}
                          whileTap={{ scale: hasChanges ? 0.98 : 1 }}
                          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                            hasChanges && !isSaving
                              ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {isSaving ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiSave} className="w-4 h-4" />
                              Save Changes
                            </>
                          )}
                        </motion.button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {activeTab === 'security' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Security Settings</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">Change Password</h3>
                            <p className="text-gray-600 text-sm mt-1">Update your account password</p>
                          </div>
                          <button
                            onClick={() => setShowPasswordChange(!showPasswordChange)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            {showPasswordChange ? 'Cancel' : 'Change'}
                          </button>
                        </div>
                        
                        {showPasswordChange && (
                          <form onSubmit={handlePasswordChange} className="mt-6 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Current Password
                              </label>
                              <div className="relative">
                                <input
                                  type={passwordVisible.current ? "text" : "password"}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                                  value={passwordData.currentPassword}
                                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                  required
                                />
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility('current')}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <SafeIcon 
                                    icon={passwordVisible.current ? FiEyeOff : FiEye} 
                                    className="w-4 h-4" 
                                  />
                                </button>
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                New Password
                              </label>
                              <div className="relative">
                                <input
                                  type={passwordVisible.new ? "text" : "password"}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                                  value={passwordData.newPassword}
                                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                  required
                                  minLength="8"
                                />
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility('new')}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <SafeIcon 
                                    icon={passwordVisible.new ? FiEyeOff : FiEye} 
                                    className="w-4 h-4" 
                                  />
                                </button>
                              </div>
                              <p className="text-gray-500 text-xs mt-1">Must be at least 8 characters</p>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Confirm New Password
                              </label>
                              <div className="relative">
                                <input
                                  type={passwordVisible.confirm ? "text" : "password"}
                                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                                  value={passwordData.confirmPassword}
                                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                  required
                                />
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility('confirm')}
                                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <SafeIcon 
                                    icon={passwordVisible.confirm ? FiEyeOff : FiEye} 
                                    className="w-4 h-4" 
                                  />
                                </button>
                              </div>
                            </div>
                            
                            <div className="flex justify-end">
                              <button
                                type="submit"
                                disabled={isSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                              >
                                {isSaving ? 'Updating...' : 'Update Password'}
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">Two-Factor Authentication</h3>
                            <p className="text-gray-600 text-sm mt-1">Add an extra layer of security</p>
                          </div>
                          <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                            Enable
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">Active Sessions</h3>
                            <p className="text-gray-600 text-sm mt-1">Manage your active sessions</p>
                          </div>
                          <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                            Sign Out All
                          </button>
                        </div>
                        
                        <div className="mt-4 space-y-3">
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div>
                              <p className="font-medium text-gray-900">Current Session</p>
                              <p className="text-gray-600 text-sm">Chrome on macOS • New York, NY</p>
                            </div>
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Active
                            </span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div>
                              <p className="font-medium text-gray-900">Mobile App</p>
                              <p className="text-gray-600 text-sm">iOS • San Francisco, CA</p>
                            </div>
                            <button className="text-red-600 hover:text-red-700 text-sm">
                              Sign Out
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-900">Security History</h3>
                            <p className="text-gray-600 text-sm mt-1">Recent security events</p>
                          </div>
                        </div>
                        
                        <div className="mt-4 space-y-3">
                          <div className="flex items-center p-3 bg-white rounded-lg border">
                            <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">Successful login</p>
                              <p className="text-gray-600 text-sm">Today at 9:45 AM from Chrome on macOS</p>
                            </div>
                          </div>
                          <div className="flex items-center p-3 bg-white rounded-lg border">
                            <SafeIcon icon={FiAlertCircle} className="text-yellow-500 mr-3" />
                            <div>
                              <p className="font-medium text-gray-900">Password change</p>
                              <p className="text-gray-600 text-sm">Yesterday at 3:22 PM</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'notifications' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Communication Channels</h3>
                        <div className="space-y-4">
                          {[
                            { id: 'emailNotifications', label: 'Email Notifications', description: 'Receive important updates via email' },
                            { id: 'smsNotifications', label: 'SMS Notifications', description: 'Get critical alerts via text message' }
                          ].map((item) => (
                            <div key={item.id} className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{item.label}</p>
                                <p className="text-gray-600 text-sm">{item.description}</p>
                              </div>
                              <button
                                onClick={() => handleNotificationChange(item.id)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  notificationSettings[item.id] ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    notificationSettings[item.id] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Notification Types</h3>
                        <div className="space-y-4">
                          {[
                            { id: 'referralUpdates', label: 'Referral Updates', description: 'Notifications about new referrals and status changes' },
                            { id: 'dealroomActivity', label: 'Dealroom Activity', description: 'Updates about documents and messages in dealrooms' },
                            { id: 'communityMentions', label: 'Community Mentions', description: 'When someone mentions you in the community' },
                            { id: 'appointmentReminders', label: 'Appointment Reminders', description: 'Reminders for upcoming appointments' },
                            { id: 'paymentReminders', label: 'Payment Reminders', description: 'Notifications about upcoming payments' },
                            { id: 'newsletter', label: 'Newsletter', description: 'Monthly updates and industry news' },
                            { id: 'securityAlerts', label: 'Security Alerts', description: 'Important security-related notifications' }
                          ].map((item) => (
                            <div key={item.id} className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{item.label}</p>
                                <p className="text-gray-600 text-sm">{item.description}</p>
                              </div>
                              <button
                                onClick={() => handleNotificationChange(item.id)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  notificationSettings[item.id] ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    notificationSettings[item.id] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Notification Schedule</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Daily Digest Time
                            </label>
                            <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                              <option>9:00 AM</option>
                              <option>12:00 PM</option>
                              <option>5:00 PM</option>
                              <option>8:00 PM</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Quiet Hours
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Start Time</label>
                                <input 
                                  type="time" 
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">End Time</label>
                                <input 
                                  type="time" 
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'privacy' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Privacy Settings</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Profile Visibility</h3>
                        <div className="space-y-3">
                          {[
                            { value: 'public', label: 'Public', description: 'Anyone can see your profile' },
                            { value: 'network', label: 'Network Only', description: 'Only your professional network can see your profile' },
                            { value: 'private', label: 'Private', description: 'Only you can see your profile' }
                          ].map((option) => (
                            <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="radio"
                                name="profileVisibility"
                                className="mt-1"
                                checked={privacySettings.profileVisibility === option.value}
                                onChange={() => handlePrivacyChange('profileVisibility', option.value)}
                              />
                              <div>
                                <p className="font-medium text-gray-900">{option.label}</p>
                                <p className="text-gray-600 text-sm">{option.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Personal Information</h3>
                        <div className="space-y-4">
                          {[
                            { id: 'showEmail', label: 'Show Email', description: 'Display your email address on your profile' },
                            { id: 'showPhone', label: 'Show Phone Number', description: 'Display your phone number on your profile' },
                            { id: 'showInDirectory', label: 'Show in Directory', description: 'Appear in the public provider directory' },
                            { id: 'allowMessaging', label: 'Allow Messaging', description: 'Allow other users to send you messages' }
                          ].map((item) => (
                            <div key={item.id} className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{item.label}</p>
                                <p className="text-gray-600 text-sm">{item.description}</p>
                              </div>
                              <button
                                onClick={() => handlePrivacyChange(item.id, !privacySettings[item.id])}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  privacySettings[item.id] ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    privacySettings[item.id] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Activity Status</h3>
                        <div className="space-y-3">
                          {[
                            { value: 'online', label: 'Online', description: 'Show when you are active on the platform' },
                            { value: 'away', label: 'Away', description: 'Show when you are inactive for 15+ minutes' },
                            { value: 'invisible', label: 'Invisible', description: 'Hide your online status from others' }
                          ].map((option) => (
                            <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="radio"
                                name="activityStatus"
                                className="mt-1"
                                checked={privacySettings.activityStatus === option.value}
                                onChange={() => handlePrivacyChange('activityStatus', option.value)}
                              />
                              <div>
                                <p className="font-medium text-gray-900">{option.label}</p>
                                <p className="text-gray-600 text-sm">{option.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Data Management</h3>
                        <div className="space-y-4">
                          <button className="w-full text-left p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">Download Your Data</p>
                                <p className="text-gray-600 text-sm">Get a copy of your information</p>
                              </div>
                              <SafeIcon icon={FiActivity} className="w-5 h-5 text-gray-400" />
                            </div>
                          </button>
                          <button 
                            onClick={handleDeleteAccount}
                            className="w-full text-left p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">Delete Account</p>
                                <p className="text-gray-600 text-sm">Permanently remove your account</p>
                              </div>
                              <SafeIcon icon={FiX} className="w-5 h-5 text-red-500" />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'billing' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Billing & Subscription</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Current Plan</h3>
                        <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium text-gray-900">{subscription.plan} Plan</p>
                            <p className="text-gray-600 text-sm">
                              Next billing: {subscription.nextBilling} ({subscription.amount})
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            subscription.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {subscription.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Payment Method</h3>
                        <div className="p-4 bg-white rounded-lg border">
                          <div className="flex items-center">
                            <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center mr-3">
                              <SafeIcon icon={FiCreditCard} className="text-gray-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{billingInfo.cardNumber}</p>
                              <p className="text-gray-600 text-sm">Expires {billingInfo.expiryDate}</p>
                            </div>
                          </div>
                          <div className="mt-3 text-sm text-gray-600">
                            <p>{billingInfo.cardHolder}</p>
                            <p>{billingInfo.billingAddress}</p>
                          </div>
                        </div>
                        <div className="mt-4 flex space-x-3">
                          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            Update Payment Method
                          </button>
                          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                            Add Backup Payment
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Billing History</h3>
                        <div className="space-y-3">
                          {[1, 2, 3].map((item) => (
                            <div key={item} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                              <div>
                                <p className="font-medium text-gray-900">Subscription Payment</p>
                                <p className="text-gray-600 text-sm">Nov {15 - item}, 2024</p>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-gray-900">$99.99</p>
                                <button className="text-blue-600 text-sm hover:text-blue-800">
                                  View Receipt
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Plan Management</h3>
                        <div className="space-y-3">
                          <button className="w-full text-left p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">Change Plan</p>
                                <p className="text-gray-600 text-sm">Upgrade or downgrade your subscription</p>
                              </div>
                              <SafeIcon icon={FiBook} className="w-5 h-5 text-gray-400" />
                            </div>
                          </button>
                          <button className="w-full text-left p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">Cancel Subscription</p>
                                <p className="text-gray-600 text-sm">Cancel your subscription at the end of billing period</p>
                              </div>
                              <SafeIcon icon={FiX} className="w-5 h-5 text-red-500" />
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === 'account' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Management</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Account Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-white rounded-lg border">
                            <p className="text-sm text-gray-600">Account ID</p>
                            <p className="font-medium">ACC-{Math.floor(100000 + Math.random() * 900000)}</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <p className="text-sm text-gray-600">Member Since</p>
                            <p className="font-medium">January 15, 2022</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <p className="text-sm text-gray-600">Last Login</p>
                            <p className="font-medium">Today at 9:45 AM</p>
                          </div>
                          <div className="p-4 bg-white rounded-lg border">
                            <p className="text-sm text-gray-600">Account Status</p>
                            <p className="font-medium text-green-600">Active</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Connected Accounts</h3>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-blue-600 font-bold">G</span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Google</p>
                                <p className="text-gray-600 text-sm">sarah.mitchell@gmail.com</p>
                              </div>
                            </div>
                            <button className="text-red-600 hover:text-red-800 text-sm">
                              Disconnect
                            </button>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                                <span className="text-white font-bold">L</span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">LinkedIn</p>
                                <p className="text-gray-600 text-sm">sarah-mitchell-dental</p>
                              </div>
                            </div>
                            <button className="text-red-600 hover:text-red-800 text-sm">
                              Disconnect
                            </button>
                          </div>
                        </div>
                        <button className="mt-4 w-full py-2 border border-dashed border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                          Connect New Account
                        </button>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Export Data</h3>
                        <p className="text-gray-600 mb-4">
                          Download a copy of your data in various formats
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <button className="p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors text-center">
                            <SafeIcon icon={FiBook} className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="font-medium text-gray-900">CSV</p>
                            <p className="text-gray-600 text-sm">Spreadsheet</p>
                          </button>
                          <button className="p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors text-center">
                            <SafeIcon icon={FiActivity} className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="font-medium text-gray-900">JSON</p>
                            <p className="text-gray-600 text-sm">Structured Data</p>
                          </button>
                          <button className="p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors text-center">
                            <SafeIcon icon={FiLink} className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <p className="font-medium text-gray-900">PDF</p>
                            <p className="text-gray-600 text-sm">Document</p>
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="font-medium text-gray-900 mb-4">Need Help?</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button className="p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors text-left">
                            <div className="flex items-center">
                              <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-blue-600 mr-2" />
                              <p className="font-medium text-gray-900">Help Center</p>
                            </div>
                            <p className="text-gray-600 text-sm mt-2">
                              Browse our knowledge base for answers to common questions
                            </p>
                          </button>
                          <button className="p-4 bg-white rounded-lg border hover:bg-gray-50 transition-colors text-left">
                            <div className="flex items-center">
                              <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600 mr-2" />
                              <p className="font-medium text-gray-900">Contact Support</p>
                            </div>
                            <p className="text-gray-600 text-sm mt-2">
                              Get in touch with our support team for personalized assistance
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;